import type { ReactNode } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { Card, IconButton, PixelPlaceholderLoader } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { openHooksModalV5 } from "app/services/ModalService";

import { CONVERSION_RATE_TRANSLATION_KEYS } from "../consts";

import { ConversionRateCardHeader } from "./ConversionRateCardHeader";

export type ConversionRateCardState = "locked" | "loading" | "available";

interface IConversionRateCardShellProps {
    title: string;
    tooltip: string;
    dateRangeText: string;
    /** When provided, the header renders two date items separated by `separatorText`. */
    compareDateRangeText?: string;
    separatorText?: string;
    countryCode: number;
    countryText: string;
    state: ConversionRateCardState;
    cardAutomationId: string;
    lockedCtaAutomationId: string;
    loadingRowCount?: number;
    children?: ReactNode;
}

export const ConversionRateCardShell = ({
    title,
    tooltip,
    dateRangeText,
    compareDateRangeText,
    separatorText,
    countryCode,
    countryText,
    state,
    cardAutomationId,
    lockedCtaAutomationId,
    loadingRowCount = 1,
    children,
}: IConversionRateCardShellProps) => {
    const { translate } = useTranslate();

    return (
        <Card
            data-automation={cardAutomationId}
            flex="1"
            minWidth="0"
        >
            <ConversionRateCardHeader
                title={title}
                tooltip={tooltip}
                dateRangeText={dateRangeText}
                compareDateRangeText={compareDateRangeText}
                separatorText={separatorText}
                countryCode={countryCode}
                countryText={countryText}
                automationPrefix={cardAutomationId}
            />
            {state === "locked" ? (
                <Flex
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    textAlign="center"
                    padding="24px"
                    gap="8px"
                    flex="1"
                >
                    <Text fontVariant="subtitle2">{translate(CONVERSION_RATE_TRANSLATION_KEYS.lockedTitle)}</Text>
                    <IconButton
                        type="trial"
                        iconName="sparkles"
                        onClick={() => openHooksModalV5("conversionRate")}
                        data-automation={lockedCtaAutomationId}
                    >
                        {translate(CONVERSION_RATE_TRANSLATION_KEYS.lockedCta)}
                    </IconButton>
                </Flex>
            ) : null}
            {state === "loading" ? (
                <Flex
                    flexDirection="column"
                    justifyContent="center"
                    padding="16px 24px"
                    gap="8px"
                    flex="1"
                >
                    <PixelPlaceholderLoader
                        width="100%"
                        height={40}
                        borderRadius={4}
                    />
                    {Array.from({ length: loadingRowCount }).map((_, index) => (
                        <PixelPlaceholderLoader
                            key={index}
                            width="100%"
                            height={20}
                            borderRadius={4}
                        />
                    ))}
                </Flex>
            ) : null}
            {state === "available" ? children : null}
        </Card>
    );
};
